import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RefundVouchersService } from '../../../services/refund-vouchers.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { BanksService } from '../../../services/banks.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-view-refund-voucher',
  templateUrl: './view-refund-voucher.component.html',
  styleUrls: ['./view-refund-voucher.component.scss']
})
export class ViewRefundVoucherComponent implements OnInit {
  refundId: number = 0;
  refundVoucher: any = null;
  auditTrail: any[] = [];

  // Variables para detalles de RefundVoucherItems
  refundVoucherItems: any[] = [];
  showItemDetails: boolean = false;

  // 🆕 Lista de bancos disponibles para seleccionar al procesar
  banks: any[] = [];

  // Forms for different actions
  approvalForm: FormGroup;
  processForm: FormGroup;
  cancelForm: FormGroup;

  // Modal visibility flags
  showApprovalModal: boolean = false;
  showProcessModal: boolean = false;
  showCancelModal: boolean = false;

  // PDF download state
  isDownloadingPdf: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private refundVouchersService: RefundVouchersService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private banksService: BanksService,
    private fb: FormBuilder
  ) {
    this.approvalForm = this.fb.group({
      isApproved: [true],
      reviewedBy: ['', [Validators.required, Validators.maxLength(100)]],
      reviewComments: ['', [Validators.maxLength(1000)]],
      rejectionReason: [''],
      approvedAmount: ['']
    });

    this.processForm = this.fb.group({
      amountToProcess: ['', [Validators.required, Validators.min(0.01)]],
      bankTransactionReference: ['', [Validators.maxLength(100)]],
      processingObservations: ['', [Validators.maxLength(1000)]],
      processedBy: ['', [Validators.required, Validators.maxLength(100)]],
      isFinalProcessing: [true],
      // 🆕 Fecha efectiva del pago bancario (editable en el modal de resumen)
      paymentDate: [this.todayIsoDate()],
      // 🆕 Banco desde el cual se paga la devolución (editable sin anular)
      bankId: [null, [Validators.required]]
    });

    // 🆕 Form de anulación: solo motivo (usuario viene de claims en backend)
    this.cancelForm = this.fb.group({
      reason: ['', [Validators.required, Validators.maxLength(500)]]
    });
  }

  /** 🆕 Helper para default de PaymentDate (YYYY-MM-DD para input type=date) */
  private todayIsoDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  downloadPdf(): void {
    if (this.isDownloadingPdf || !this.refundVoucher) return;
    this.isDownloadingPdf = true;
    this.refundVouchersService.downloadPdf(this.refundId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Devolucion_${this.refundId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPdf = false;
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.toastService.showError('No se pudo descargar el PDF');
        this.isDownloadingPdf = false;
      }
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.refundId = +params['id'];
      this.loadRefundVoucher();
      this.loadAuditTrail();
      this.loadBanks();
    });
  }

  loadBanks(): void {
    this.banksService.getAll().subscribe({
      next: (response) => {
        if (response?.isSuccess) {
          this.banks = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        }
      },
      error: (error) => {
        console.error('Error loading banks:', error);
      }
    });
  }

  loadRefundVoucher(): void {
    this.loadingService.show();
    this.refundVouchersService.getById(this.refundId).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.refundVoucher = response.data;
          
          // Set default values for forms based on loaded data
          this.processForm.get('amountToProcess')?.setValue(this.refundVoucher.pendingAmount);
          
          // Set user name if available (in a real app, get from auth service)
          const currentUser = 'Usuario Actual'; // Replace with actual user from auth service
          this.approvalForm.get('reviewedBy')?.setValue(currentUser);
          this.processForm.get('processedBy')?.setValue(currentUser);
          // 🆕 cancelForm ya no tiene cancelledBy — viene de los claims del backend
        } else {
          this.toastService.showError('Error al cargar la devolución');
          this.router.navigate(['/refund-vouchers']);
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading refund voucher:', error);
        this.toastService.showError('Error al cargar la devolución');
        this.loadingService.hide();
        this.router.navigate(['/refund-vouchers']);
      }
    });
  }

  loadAuditTrail(): void {
    this.refundVouchersService.getAuditTrail(this.refundId).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.auditTrail = response.data;
        }
      },
      error: (error) => {
        console.error('Error loading audit trail:', error);
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Approved': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Processing': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'Cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }

  /**
   * Verifica si la devolución está completada o cancelada
   */
  isCompletedOrCancelled(): boolean {
    if (!this.refundVoucher) return false;
    const status = this.refundVoucher.status;
    return status === 'Completed' || status === 'Cancelled' || status === 'Rejected';
  }

  /**
   * Verifica si se deben mostrar los botones de acción
   */
  shouldShowActionButtons(): boolean {
    return !this.isCompletedOrCancelled();
  }

  // Modal control methods
  openApprovalModal(): void {
    this.showApprovalModal = true;
    
    // Reset form and set default values
    this.approvalForm.reset({
      isApproved: true,
      reviewedBy: 'Usuario Actual', // Replace with actual user
      reviewComments: '',
      rejectionReason: '',
      approvedAmount: this.refundVoucher.refundAmount
    });
  }
  
  openProcessModal(): void {
    this.showProcessModal = true;

    // Reset form and set default values
    this.processForm.reset({
      amountToProcess: this.refundVoucher.pendingAmount,
      bankTransactionReference: this.refundVoucher.bankTransactionReference || '',
      processingObservations: '',
      processedBy: 'Usuario Actual', // Replace with actual user
      isFinalProcessing: true,
      // 🆕 Fecha de pago prellenada con hoy (el usuario puede editarla)
      paymentDate: this.todayIsoDate(),
      // 🆕 Banco prellenado con el actual del comprobante (editable)
      bankId: this.refundVoucher.bankId ?? this.refundVoucher.bank?.id ?? null
    });
  }

  openCancelModal(): void {
    this.showCancelModal = true;

    // 🆕 Reset form: solo reason (el backend usa claims para cancelledBy)
    this.cancelForm.reset({
      reason: ''
    });
  }
  
  closeModals(): void {
    this.showApprovalModal = false;
    this.showProcessModal = false;
    this.showCancelModal = false;
  }

  // Form submission methods
  submitApproval(): void {
    if (this.approvalForm.invalid) {
      this.markFormGroupTouched(this.approvalForm);
      return;
    }
    
    // If rejection, ensure rejection reason is provided
    if (!this.approvalForm.value.isApproved && !this.approvalForm.value.rejectionReason) {
      this.approvalForm.get('rejectionReason')?.setErrors({ 'required': true });
      this.markFormGroupTouched(this.approvalForm);
      return;
    }
    
    this.loadingService.show();
    this.refundVouchersService.processApproval(this.refundId, this.approvalForm.value).subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.isSuccess) {
          this.toastService.showSuccess(
            this.approvalForm.value.isApproved ? 'Devolución aprobada exitosamente' : 'Devolución rechazada exitosamente'
          );
          this.closeModals();
          this.loadRefundVoucher();
          this.loadAuditTrail();
        } else {
          this.toastService.showError(response.message || 'Error al procesar la aprobación');
        }
      },
      error: (error) => {
        this.loadingService.hide();
        console.error('Error processing approval:', error);
        this.toastService.showError('Error al procesar la aprobación');
      }
    });
  }
  
  submitProcess(): void {
    if (this.processForm.invalid) {
      this.markFormGroupTouched(this.processForm);
      return;
    }

    // 🆕 Serializar paymentDate como ISO (o null si el input está vacío) y normalizar bankId
    const raw = this.processForm.value;
    const bankIdNum = raw.bankId !== null && raw.bankId !== undefined && raw.bankId !== ''
      ? Number(raw.bankId)
      : null;
    const payload = {
      ...raw,
      paymentDate: raw.paymentDate ? new Date(raw.paymentDate).toISOString() : null,
      bankId: bankIdNum && bankIdNum > 0 ? bankIdNum : null
    };

    this.loadingService.show();
    this.refundVouchersService.processRefund(this.refundId, payload).subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.isSuccess) {
          this.toastService.showSuccess('Devolución procesada exitosamente');
          this.closeModals();
          this.loadRefundVoucher();
          this.loadAuditTrail();
        } else {
          this.toastService.showError(response.message || 'Error al procesar la devolución');
        }
      },
      error: (error) => {
        this.loadingService.hide();
        console.error('Error processing refund:', error);
        this.toastService.showError('Error al procesar la devolución');
      }
    });
  }
  
  submitCancel(): void {
    if (this.cancelForm.invalid) {
      this.markFormGroupTouched(this.cancelForm);
      return;
    }

    // 🆕 Payload simplificado: solo reason (el backend usa claims para CancelledBy)
    const payload = { reason: this.cancelForm.value.reason };

    this.loadingService.show();
    this.refundVouchersService.cancelRefund(this.refundId, payload).subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.isSuccess) {
          this.toastService.showSuccess('Devolución anulada exitosamente');
          this.closeModals();
          this.loadRefundVoucher();
          this.loadAuditTrail();
        } else {
          this.toastService.showError(response.message || 'Error al anular la devolución');
        }
      },
      error: (error) => {
        this.loadingService.hide();
        console.error('Error cancelling refund:', error);
        this.toastService.showError('Error al anular la devolución');
      }
    });
  }
  
  // Helper to mark all controls as touched for validation display
  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
  
  // Approval form specific methods
  onApprovalStatusChange(): void {
    const isApproved = this.approvalForm.get('isApproved')?.value;
    const rejectionReasonControl = this.approvalForm.get('rejectionReason');
    const approvedAmountControl = this.approvalForm.get('approvedAmount');
    
    if (isApproved) {
      rejectionReasonControl?.clearValidators();
      approvedAmountControl?.enable();
    } else {
      rejectionReasonControl?.setValidators([Validators.required, Validators.maxLength(500)]);
      approvedAmountControl?.disable();
    }
    
    rejectionReasonControl?.updateValueAndValidity();
  }
  
  // Métodos para manejar RefundVoucherItems y detalles de descuentos
  
  /**
   * Obtiene y procesa los RefundVoucherItems con sus detalles
   */
  getRefundVoucherItems(): any[] {
    if (!this.refundVoucher?.refundVoucherItems) {
      return [];
    }
    
    return this.refundVoucher.refundVoucherItems.map((item: any) => {
      let parsedDetails = null;
      
      try {
        // Intentar parsear los detalles JSON de Observations
        if (item.observations && item.observations.startsWith('{')) {
          parsedDetails = JSON.parse(item.observations);
        }
      } catch (e) {
        console.warn('Error parsing RefundVoucherItem observations:', e);
      }
      
      return {
        ...item,
        parsedDetails: parsedDetails,
        hasDetailedInfo: !!parsedDetails
      };
    });
  }
  
  /**
   * Obtiene el tipo de documento legible
   */
  getDocumentTypeDescription(documentType: string): string {
    switch (documentType) {
      case 'ExcessPayment': return 'Excedente de Pago';
      case 'IncomeReceipt': return 'Comprobante de Ingreso';
      default: return documentType;
    }
  }
  
  /**
   * Calcula el total de descuentos/fees de un IncomeReceipt
   */
  getTotalFeesFromIncomeReceipt(incomeReceipt: any): number {
    if (!incomeReceipt?.feeDetails) {
      return incomeReceipt?.totalFees || 0;
    }
    
    return incomeReceipt.feeDetails.reduce((total: number, fee: any) => {
      return total + (fee.amount || 0);
    }, 0);
  }
  
  /**
   * Obtiene descripción de los tipos de fees/descuentos
   */
  getFeeTypeDescription(feeTypeId: number, description: string): string {
    // Mapeo básico de tipos de fees comunes
    const feeTypeMap: { [key: number]: string } = {
      1: 'Comisión Bancaria',
      2: 'Descuento por Pronto Pago',
      3: 'Comisión Gateway',
      4: 'Impuesto',
      5: 'Cargo Administrativo'
    };
    
    return feeTypeMap[feeTypeId] || description || `Fee Tipo ${feeTypeId}`;
  }
  
  /**
   * Togglea la visualización de detalles de items
   */
  toggleItemDetails(): void {
    this.showItemDetails = !this.showItemDetails;
  }
  
  /**
   * Verifica si el refund está relacionado con excedentes
   */
  hasExcessPaymentRelationship(): boolean {
    return !!this.refundVoucher?.excessPayment || 
           this.getRefundVoucherItems().some(item => item.documentType === 'ExcessPayment');
  }
  
  /**
   * Obtiene el resumen financiero total del refund
   */
  getFinancialSummary(): any {
    const items = this.getRefundVoucherItems();
    
    let totalGrossAmount = 0;
    let totalNetAmount = 0;
    let totalFees = 0;
    let totalRefundableAmount = 0;
    let totalReceivedAmount = 0;
    let hasExcessPayments = false;
    
    items.forEach(item => {
      totalRefundableAmount += item.refundableAmount || 0;
      
      // ✅ MEJORADO: Incluir información de excedentes
      if (item.documentType === 'ExcessPayment' && item.excessPaymentDetail?.originIncomeReceipt) {
        hasExcessPayments = true;
        const originReceipt = item.excessPaymentDetail.originIncomeReceipt;
        totalGrossAmount += originReceipt.grossAmount || 0;
        totalNetAmount += originReceipt.netAmount || 0;
        totalFees += originReceipt.totalFees || 0;
        totalReceivedAmount += originReceipt.receivedAmount || 0;
      }
      
      // Para comprobantes de ingreso directos
      if (item.incomeReceipts?.length > 0) {
        item.incomeReceipts.forEach((ir: any) => {
          totalGrossAmount += ir.grossAmount || 0;
          totalNetAmount += ir.netAmount || 0;
          totalFees += ir.totalFees || 0;
          totalReceivedAmount += ir.receivedAmount || 0;
        });
      }
      
      // Para JSON en observations (legacy)
      if (item.parsedDetails?.IncomeReceipts) {
        item.parsedDetails.IncomeReceipts.forEach((ir: any) => {
          totalGrossAmount += ir.GrossAmount || 0;
          totalNetAmount += ir.NetAmount || 0;
          totalFees += ir.TotalFees || 0;
        });
      }
    });
    
    return {
      totalGrossAmount,
      totalNetAmount,
      totalFees,
      totalRefundableAmount,
      totalReceivedAmount,
      hasExcessPayments,
      discountPercentage: totalGrossAmount > 0 ? (totalFees / totalGrossAmount) * 100 : 0
    };
  }

  /**
   * Verifica si es una devolución de servicio
   */
  isServiceRefund(): boolean {
    if (!this.refundVoucher) return false;
    
    // Verificar si es una devolución de servicio por el tipo o por no tener excedentes/planes
    return this.refundVoucher.refundTypeDescription?.includes('Servicio') ||
           this.refundVoucher.refundTypeDescription?.includes('servicio') ||
           (!this.refundVoucher.excessPayment && !this.refundVoucher.paymentPlan);
  }

  /**
   * Obtiene información del comprobante de ingreso origen para devoluciones de servicio
   */
  getServiceRefundOriginReceipt(): any {
    if (!this.isServiceRefund()) {
      return null;
    }

    // Buscar en RefundVoucherItems el primer comprobante de ingreso
    const items = this.getRefundVoucherItems();
    for (const item of items) {
      if (item.documentType === 'IncomeReceipt' && item.incomeReceipts?.length > 0) {
        return item.incomeReceipts[0]; // Retornar el primer comprobante de ingreso del item
      }
    }

    return null;
  }

  /**
   * Obtiene información del excedente de pago para mostrar en el resumen
   */
  getExcessPaymentInfo(): any {
    const items = this.getRefundVoucherItems();
    for (const item of items) {
      if (item.documentType === 'ExcessPayment' && item.excessPaymentDetail) {
        return {
          excessDetail: item.excessPaymentDetail,
          originReceipt: item.excessPaymentDetail.originIncomeReceipt,
          refundableAmount: item.refundableAmount
        };
      }
    }
    return null;
  }

  /**
   * Verifica si la devolución es de excedente
   */
  isExcessRefund(): boolean {
    return this.getRefundVoucherItems().some(item => item.documentType === 'ExcessPayment');
  }

  // 🆕 Obtener código de moneda del voucher (de la inscripción, no del proyecto)
  getCurrencyCode(): string {
    // Prioridad: CurrencyCode del DTO > Enrollment.Currency.Code > CorporateEnrollment.Currency.Code > COP
    if (this.refundVoucher?.currencyCode) {
      return this.refundVoucher.currencyCode;
    }
    if (this.refundVoucher?.enrollment?.currency?.code) {
      return this.refundVoucher.enrollment.currency.code;
    }
    if (this.refundVoucher?.corporateEnrollment?.currency?.code) {
      return this.refundVoucher.corporateEnrollment.currency.code;
    }
    if (this.refundVoucher?.currency?.code) {
      return this.refundVoucher.currency.code;
    }
    return 'COP'; // Fallback
  }

  // 🆕 Obtener símbolo de moneda
  getCurrencySymbol(): string {
    const code = this.getCurrencyCode();
    switch (code) {
      case 'USD': return 'US$';
      case 'EUR': return '€';
      case 'COP':
      default: return '$';
    }
  }

  // 🆕 Formatear moneda con código
  formatCurrencyWithCode(amount: number): string {
    if (!amount && amount !== 0) return '';
    const symbol = this.getCurrencySymbol();
    const code = this.getCurrencyCode();
    return `${symbol}${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${code})`;
  }

}